import { type JSX, useContext, useState } from "react";

import { FilledArrow } from "../../../assets";
import JournalDataEditModal from "./JournalDataEditModal";
import ChartsOfAccountListEditModal from "./ChartsOfAccountListEditModal";
import {
  formatToShortDate,
  formatUniversalNumber,
  handleApiError,
} from "../../../lib/utils";
import apiRequest, { ApiResponse } from "../../../axios/useApi";
import { UIContext } from "../../../providers/UIProvider";
import { AccordionGridHeader } from "./AccordionGridHeader";
import {
  AccordionItemsRendererProps,
  GeneralLedgerChartAccountListAccodianType,
  VoucherData,
} from "../types";
import { GeneralLedgerContext } from "../../../providers/GeneralLedgerProvider";
import { TablePagination } from "../../../components/ui/table-pagination";

const AccordionItemsRenderer = ({
  tabId,
  isExpanded,
  toggleAccordion,
  level = 0,
  showEditButton = false,
  onEdit,
  onSave,
  chartsAccountListItems = [],
  journalItems = [],
  journalPagination,
  setJournalPagination,
  columns,
  JournalModalStrings,
}: AccordionItemsRendererProps): JSX.Element => {
  const { onError, onSuccess, businessId } = useContext(UIContext);
  const [isjournalModalOpen, setIsJournalModalOpen] = useState(false);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [editingItem, setEditingItem] =
    useState<GeneralLedgerChartAccountListAccodianType | null>(null);
  const [voucherData, setVoucherData] = useState<VoucherData | null>(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  // Normalize the tabId for consistent comparison
  const normalizedTabId = tabId?.toLowerCase?.() || "";
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  const { handleChangeChartListing } = useContext(GeneralLedgerContext);

  // Updat the handleEditClick function
  const handleEditClick = (item: GeneralLedgerChartAccountListAccodianType) => {
    setEditingItem(item);
    setIsChartModalOpen(true);
    if (onEdit) onEdit(item);
  };

  const handleSave = (updatedData: any) => {
    if (editingItem && onSave) {
      onSave({ ...editingItem, ...updatedData });
    }
    setIsChartModalOpen(false);
  };

  const handleOpenJournalModal = async (VoucherId: number) => {
    setIsModalLoading(true);
    try {
      const response: ApiResponse<VoucherData> = await apiRequest({
        url: `/Voucher/${businessId}/Get/${VoucherId}`,
        method: "GET",
      });

      if (response && response.success) {
        setVoucherData(response?.data || null);
        setIsJournalModalOpen(true);
        // onSuccess?.(response?.message || "Data retrieved successfully!");ProfitAndLoss
      } else {
        handleApiError(response, onError);
      }
    } catch (err) {
      onError?.(new Error("An error occurred while fetching Customers."));
    } finally {
      setIsModalLoading(false);
    }
  };

  const onPageChange = (page: number) => {
    setJournalPagination &&
      setJournalPagination({
        ...journalPagination,
        page: page,
      });
  };

  const onPageSizeChange = (size: number) => {
    setJournalPagination &&
      setJournalPagination({ ...journalPagination, pageSize: size, page: 1 });
  };
  const handleShowCategory = (
    item: GeneralLedgerChartAccountListAccodianType
  ) => {
    handleChangeChartListing(true, item);
  };
  // Recursive ChartOfAccounts component using Radix UI Accordion
  const ChartOfAccountsItem = ({
    item,
    nestLevel = 0,
    showEditButton = false,
    isFirst = false,
  }: {
    item: GeneralLedgerChartAccountListAccodianType;
    nestLevel: number;
    showEditButton: boolean;
    isFirst?: boolean;
  }) => {
    const hasChildren = item.children && item.children.length > 0;
    // Create a unique identifier by combining level and name
    const itemId = `${item.level || "level-" + nestLevel}_${item.name}`;
    const expanded = isExpanded(tabId, itemId);

    return (
      <div
        className={`${
          !isFirst || nestLevel > 0
            ? "border-t BookEZ_accordion_border_color"
            : ""
        }`}
      >
        <div
          onClick={() => hasChildren && toggleAccordion(tabId, itemId)}
          className={`px-4 py-4  group  BookEZ_font_color_gray_2 ${
            hasChildren ? "cursor-pointer" : ""
          } BookEZ_font_body_size  ${"BookEZ_font_bold"} ${
            expanded ? "BookEZ_accordion_bg_active" : "BookEZ_accordion_bg"
          }
          `}
        >
          <div
            className="grid w-full items-center"
            style={{ gridTemplateColumns: "35% 20% 20% 15% 10%" }}
          >
            <div className="flex items-center">
              <div
                style={{ paddingLeft: `${nestLevel * 24}px` }}
                className="flex items-center"
              >
                {hasChildren ? (
                  <span className="mr-2 w-4 inline-block">
                    {expanded ? (
                      <FilledArrow />
                    ) : (
                      <FilledArrow className="-rotate-90" />
                    )}
                  </span>
                ) : (
                  <span className="w-4 mr-2 inline-block"></span>
                )}
                <span className="truncate">{item.name}</span>
              </div>
            </div>
            <div
              className="px-2 truncate cursor-pointer"
              onClick={() => {
                handleShowCategory(item);
              }}

              // onClick={() => setIsCategoryModalOpen(true)}
            >
              {item.type}
            </div>
            <div
              className="px-2 truncate cursor-pointer"
              onClick={() => handleShowCategory(item)}
              // onClick={() => setIsCategoryModalOpen(true)}
            >
              {item.sub_type}
            </div>
            <div className="text-right pr-4">
              {formatUniversalNumber(Number(item.balance)) || "0.00"}
            </div>
            {/* <div className="flex justify-end pr-4">
              {showEditButton && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(item);
                  }}
                  className="cursor-pointer text-gray-500 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div> */}
          </div>
        </div>

        {hasChildren && (
          <div
            className={`transition-all duration-300 ease-in-out ${
              expanded ? "block" : "hidden"
            }`}
          >
            {item.children?.map((child, index) => (
              <ChartOfAccountsItem
                key={child.name || `${item.name}-child  -${index}`}
                item={child}
                nestLevel={nestLevel + 1}
                showEditButton={showEditButton}
                isFirst={false}
              />
            ))}
          </div>
        )}
        {isCategoryModalOpen && (
          <div className="fixed inset-0 bg-black/50 bg-opacity-50 flex justify-center items-center">
            <div className="BookEZ_card_bg p-4 rounded-md">
              <h2 className="text-2xl font-bold">Category Modal</h2>
            </div>
          </div>
        )}
      </div>
    );
  };

  // For Chart of Accounts
  if (normalizedTabId === "chartofaccounts") {
    return (
      <div className="w-full border BookEZ_accordion_border_color BookEZ_card_radius overflow-hidden">
        <AccordionGridHeader
          columns={columns}
          gridTemplateColumns="35% 20% 20% 15% 10%"
        />
        {chartsAccountListItems.map((item, index) => (
          <ChartOfAccountsItem
            key={item.name || `${item.name}-${index}`}
            item={item}
            nestLevel={0}
            showEditButton={showEditButton}
            isFirst={index === 0}
          />
        ))}
        <ChartsOfAccountListEditModal
          open={isChartModalOpen}
          setIsModalOpen={setIsChartModalOpen}
          accountData={editingItem}
          onSave={handleSave}
        />
      </div>
    );
  }

  // For Journal
  if (normalizedTabId === "journal") {
    // Journal Item component
    const JournalItem = ({
      item,
      isFirst = false,
      isLast = false,
    }: {
      item: any;
      isFirst?: boolean;
      isLast?: boolean;
    }) => {
      const hasChildren = item.children && item.children.length > 0;
      const expanded = isExpanded(tabId, item.id);

      return (
        <>
          <div
            className={`${
              !isFirst ? "border-t BookEZ_accordion_border_color" : ""
            } ${isLast ? "border-b BookEZ_accordion_border_color" : ""}`}
          >
            <div
              onClick={() => {
                handleOpenJournalModal(item.voucherId);
                // hasChildren && toggleAccordion(tabId, item.id)
              }}
              className={`px-4 py-4 group BookEZ_font_color_gray_2 ${
                hasChildren ? "cursor-pointer" : ""
              } BookEZ_font_body_size BookEZ_font_bold BookEZ_accordion_bg
              
                `}
            >
              <div
                className="grid w-full items-center"
                style={{ gridTemplateColumns: "8% 15% 25% 16% 12% 12% 10%" }}
              >
                <div
                  className="flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    hasChildren && toggleAccordion(tabId, item.id);
                  }}
                >
                  {hasChildren ? (
                    <span className="mr-2 w-4 inline-block">
                      {expanded ? (
                        <FilledArrow />
                      ) : (
                        <FilledArrow className="-rotate-90" />
                      )}
                    </span>
                  ) : (
                    <span className="w-4 mr-2 inline-block"></span>
                  )}
                  <span className="truncate">{item.voucherId}</span>
                </div>
                <div className="truncate">
                  {formatToShortDate(item.voucherDate)}
                </div>
                <div className="truncate">{item.transactionType || "-"}</div>
                <div className="truncate">{item.accountName || "-"}</div>
                <div className="text-right">
                  {formatUniversalNumber(item.debitAmt)}
                </div>
                <div className="text-right">
                  {formatUniversalNumber(item.creditAmt)}
                </div>
                {/* <div className="text-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenJournalModal();
                    }}
                    className="cursor-pointer text-gray-500 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div> */}
              </div>
            </div>

            {hasChildren && (
              <div
                className={`transition-all duration-300 BookEZ_font_bold ease-in-out BookEZ_font_color_gray_2 ${
                  expanded ? "block " : "hidden"
                }`}
              >
                {item.children?.map((child: any, index: number) => (
                  <div
                    key={`${item.id}-child-${index}`}
                    className={`border-t BookEZ_accordion_border_color border-b BookEZ_card_bg`}
                  >
                    <div className="px-4 py-3 BookEZ_font_body_size group">
                      <div
                        className="grid w-full items-center"
                        style={{
                          gridTemplateColumns: "8% 15% 25% 16% 12% 12% 10%",
                        }}
                      >
                        <div className="flex items-center pl-6"></div>
                        <div></div>
                        <div></div>
                        <div className="truncate">{child.accountName}</div>
                        <div className="text-right">
                          {formatUniversalNumber(child.debitAmt)}
                        </div>
                        <div className="text-right">
                          {formatUniversalNumber(child.creditAmt)}
                        </div>
                        {/* <div className="text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenJournalModal();
                            }}
                            className="cursor-pointer text-gray-500 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </div> */}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      );
    };

    return (
      <div className="w-full">
        <div className="min-w-[800px] border-t border-l border-r BookEZ_accordion_border_color rounded-lg">
          <AccordionGridHeader
            columns={columns}
            gridTemplateColumns="8% 15% 25% 16% 12% 12% 10%"
          />
          {journalItems.map((item: any, index: number) => (
            <JournalItem
              key={item.id || `journal-item-${index}`}
              item={item}
              isFirst={index === 0}
              isLast={index === journalItems.length - 1}
            />
          ))}
        </div>
        <TablePagination
          className="BookEZ_accordion_border_color!"
          data={journalItems}
          pageSize={journalPagination?.pageSize || 10}
          currentPage={journalPagination?.page || 1}
          totalPages={journalPagination?.totalPage || 1}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          totalRecords={journalPagination?.totalRecords || 0}
        />

        <JournalDataEditModal
          open={isjournalModalOpen}
          setIsJournalModalOpen={setIsJournalModalOpen}
          voucherData={voucherData}
          isLoading={isModalLoading}
          JournalModalStrings={JournalModalStrings}
        />
      </div>
    );
  }

  // Default fallback if no matching tab
  return (
    <div className="p-4 text-center BookEZ_font_color_gray_3">
      No data available for this tab (unknown tab type: {tabId})
    </div>
  );
};

export default AccordionItemsRenderer;
